import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { serverResponse } from "../../../util/fakeServer";
import { BREAKPOINT } from "../../../helper/constant";
import { scrollToSlide } from "../../../helper/utility";
import {
  setIndexCarousel,
  setVerticalPages,
} from "../../../redux/carousel/action";
import useCurrentWidth from "../../../hooks/widthHook";
import "./CategoryMenu.css";

library.add(fas);

const renderIcon = (icon) => {
  if (!icon) return null;
  if (/^https?:\/\//i.test(icon) || icon.startsWith("/")) {
    return <img src={icon} alt="" className="category-icon-img" />;
  }
  if (icon.startsWith("fa-")) {
    const name = icon.replace(/^fa-/, "");
    return <FontAwesomeIcon icon={["fas", name]} />;
  }
  return icon;
};

const pageToCarouselIndex = (page, isMobile) => {
  if (isMobile || serverResponse.config.show_one_page) {
    return serverResponse.leaflet.index === null ? page - 1 : page;
  }
  return parseInt(page / 2, 10);
};

const CategoryMenu = (props) => {
  const width = useCurrentWidth();
  const isMobile = width < BREAKPOINT.xs;
  const target = props.target;
  const [open, setOpen] = useState(false);
  const [visiblePage, setVisiblePage] = useState(null);
  const listRef = useRef(null);
  const itemRefs = useRef({});
  const barRef = useRef(null);
  const chipRefs = useRef({});
  const pendingTargetPageRef = useRef(null);
  const pendingTimeoutRef = useRef(null);

  const categories = serverResponse.leaflet.categoryIndex;

  const sortedCategories = useMemo(() => {
    if (!Array.isArray(categories)) return [];
    return [...categories].sort((a, b) => (a.page || 0) - (b.page || 0));
  }, [categories]);

  const activeId = useMemo(() => {
    if (sortedCategories.length === 0) return null;
    const reference =
      visiblePage != null
        ? pageToCarouselIndex(visiblePage, isMobile)
        : props.carouselIndex;
    let match = null;
    for (const cat of sortedCategories) {
      const catCarouselIndex = pageToCarouselIndex(cat.page, isMobile);
      if (catCarouselIndex <= reference) {
        match = cat;
      } else {
        break;
      }
    }
    return match ? match.id : sortedCategories[0].id;
  }, [sortedCategories, props.carouselIndex, visiblePage, isMobile]);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close]);

  useEffect(() => {
    if (!open || !activeId || !listRef.current) return;
    const item = itemRefs.current[activeId];
    if (!item) return;
    const list = listRef.current;
    const offset =
      item.offsetTop - list.offsetHeight / 2 + item.offsetHeight / 2;
    list.scrollTo({ top: Math.max(0, offset), behavior: "smooth" });
  }, [open, activeId]);

  useEffect(() => {
    if (!isMobile || !activeId || !barRef.current) return;
    const chip = chipRefs.current[activeId];
    if (!chip) return;
    const bar = barRef.current;
    const offset =
      chip.offsetLeft - bar.offsetWidth / 2 + chip.offsetWidth / 2;
    bar.scrollTo({ left: Math.max(0, offset), behavior: "smooth" });
  }, [isMobile, activeId]);

  const hasCategories =
    Array.isArray(categories) && categories.length > 0;
  const shouldMarkBody = isMobile && hasCategories && target !== "desktop";
  const scrollModeActive =
    shouldMarkBody &&
    (serverResponse.config.scrollVertical ||
      serverResponse.config.scrollHorizontal);

  useEffect(() => {
    if (!shouldMarkBody) return;
    document.body.classList.add("has-category-menu");
    return () => {
      document.body.classList.remove("has-category-menu");
    };
  }, [shouldMarkBody]);

  useEffect(() => {
    if (!scrollModeActive) return;

    const observedSet = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        if (pendingTargetPageRef.current != null) {
          for (const e of entries) {
            if (e.intersectionRatio < 0.5) continue;
            const match = e.target.id.match(/^slide_(\d+)$/);
            if (
              match &&
              parseInt(match[1], 10) === pendingTargetPageRef.current
            ) {
              pendingTargetPageRef.current = null;
              if (pendingTimeoutRef.current) {
                clearTimeout(pendingTimeoutRef.current);
                pendingTimeoutRef.current = null;
              }
              break;
            }
          }
          return;
        }
        let best = null;
        entries.forEach((e) => {
          if (
            e.intersectionRatio >= 0.5 &&
            (!best || e.intersectionRatio > best.intersectionRatio)
          ) {
            best = e;
          }
        });
        if (best) {
          const match = best.target.id.match(/^slide_(\d+)$/);
          if (match) setVisiblePage(parseInt(match[1], 10));
        }
      },
      { threshold: [0.5, 0.75, 1] }
    );

    const observeSlides = () => {
      document.querySelectorAll('[id^="slide_"]').forEach((el) => {
        if (!observedSet.has(el)) {
          observer.observe(el);
          observedSet.add(el);
        }
      });
    };
    observeSlides();

    const mut = new MutationObserver(() => observeSlides());
    mut.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mut.disconnect();
      observedSet.clear();
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
      pendingTargetPageRef.current = null;
    };
  }, [scrollModeActive]);

  if (!Array.isArray(categories) || categories.length === 0) return null;
  if (target === "mobile" && !isMobile) return null;
  if (target === "desktop" && isMobile) return null;

  const navigate = (category) => {
    const targetIndex = pageToCarouselIndex(category.page, isMobile);
    if (scrollModeActive) {
      if (visiblePage !== category.page) {
        pendingTargetPageRef.current = category.page;
        if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = setTimeout(() => {
          pendingTargetPageRef.current = null;
          pendingTimeoutRef.current = null;
        }, 4000);
      }
      setVisiblePage(category.page);
    }
    props.setIndexCarousel(targetIndex);
    if (
      (serverResponse.config.scrollVertical ||
        serverResponse.config.scrollHorizontal) &&
      isMobile
    ) {
      scrollToSlide(
        category.page,
        props.setVerticalPages,
        props.verticalPages
      );
    }
  };

  if (isMobile) {
    return (
      <nav className="category-bar" aria-label="Categorie volantino">
        <div className="category-bar__scroller" ref={barRef}>
          {sortedCategories.map((cat) => {
            const isActive = cat.id === activeId;
            return (
              <button
                key={cat.id}
                ref={(el) => (chipRefs.current[cat.id] = el)}
                type="button"
                className={`category-bar__chip${
                  isActive ? " category-bar__chip--active" : ""
                }`}
                onClick={() => navigate(cat)}
              >
                {cat.icon ? (
                  <span className="category-bar__icon" aria-hidden="true">
                    {renderIcon(cat.icon)}
                  </span>
                ) : null}
                <span className="category-bar__label">{cat.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    );
  }

  const activeCategory = sortedCategories.find((c) => c.id === activeId);

  const handleDrawerClick = (category) => {
    navigate(category);
    close();
  };

  return (
    <>
      <button
        type="button"
        className="category-fab"
        onClick={() => setOpen(true)}
        aria-label="Apri indice"
        aria-expanded={open}
      >
        <span className="category-fab__icon" aria-hidden="true">
          {activeCategory && activeCategory.icon
            ? renderIcon(activeCategory.icon)
            : "☰"}
        </span>
        <span className="category-fab__label">Indice</span>
      </button>

      <div
        className={`category-drawer-backdrop${
          open ? " category-drawer-backdrop--visible" : ""
        }`}
        onClick={close}
        aria-hidden={!open}
      />

      <aside
        className={`category-drawer${open ? " category-drawer--open" : ""}`}
        aria-label="Categorie volantino"
        aria-hidden={!open}
      >
        <header className="category-drawer__header">
          <h3 className="category-drawer__title">Indice</h3>
          <button
            type="button"
            className="category-drawer__close"
            onClick={close}
            aria-label="Chiudi indice"
          >
            ×
          </button>
        </header>
        <ul className="category-drawer__list" ref={listRef}>
          {sortedCategories.map((cat) => {
            const isActive = cat.id === activeId;
            return (
              <li key={cat.id}>
                <button
                  ref={(el) => (itemRefs.current[cat.id] = el)}
                  type="button"
                  className={`category-drawer__item${
                    isActive ? " category-drawer__item--active" : ""
                  }`}
                  onClick={() => handleDrawerClick(cat)}
                >
                  {cat.icon ? (
                    <span
                      className="category-drawer__icon"
                      aria-hidden="true"
                    >
                      {renderIcon(cat.icon)}
                    </span>
                  ) : null}
                  <span className="category-drawer__label">{cat.label}</span>
                  <span className="category-drawer__page">p. {cat.page}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>
    </>
  );
};

const mapStateToProps = (state) => ({
  carouselIndex: state.carousel.carouselIndex,
  verticalPages: state.carousel.verticalPages,
});

export default connect(mapStateToProps, {
  setIndexCarousel,
  setVerticalPages,
})(CategoryMenu);
